import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retrying?: boolean;
  className?: string;
  /** Optional secondary action (e.g. "Contact support") */
  secondaryAction?: React.ReactNode;
}

/**
 * Standardized error state with actionable retry.
 * Use anywhere a fetch/mutation can fail and the user can recover.
 */
export const ErrorState = ({
  title = "Something didn't load",
  message = "We couldn't reach the server. Check your connection and try again.",
  onRetry,
  retrying,
  className,
  secondaryAction,
}: ErrorStateProps) => {
  return (
    <div className={cn("flex flex-col items-center justify-center text-center py-16 px-6", className)}>
      <div className="rounded-full bg-destructive/10 ring-1 ring-destructive/20 p-4 mb-4">
        <AlertCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
      </div>
      <h3 className="text-lg font-semibold text-foreground mb-1">{title}</h3>
      <p className="text-muted-foreground text-sm mb-5 max-w-md">{message}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {onRetry && (
          <Button onClick={onRetry} disabled={retrying} className="gap-2">
            <RefreshCw className={cn("h-4 w-4", retrying && "animate-spin")} />
            {retrying ? "Retrying…" : "Try again"}
          </Button>
        )}
        {secondaryAction}
      </div>
    </div>
  );
};

export default ErrorState;
